import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

import { SvgIcon, useTheme, Zoom } from "@mui/material";
import { alpha, emphasize } from "@mui/material";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { getMedicationFormPath } from "../../assets/svgGraphics";
import * as patientSessionActions from "../../state/redux/patientSession";
import { deepCopyObject } from "../../utils/helpers";
import PatientMedicationDialog from "../dialogs/patientMedicationDialog";
import OmniAvatar from "../ui/omniAvatar";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget";
import WidgetList from "../ui/widgetList";
import WidgetSkeleton from "../ui/widgetSkeleton";
import WidgetTitle from "../ui/widgetTitle";

const PatientMedications = props => {
  const { patientChart } = props;

  const [selectedMedication, setSelectedMedication] = useState(null);

  const [titleText, setTitleText] = useState(null);

  useEffect(() => {
    if (patientChart != null) {
      setTitleText(
        `${Object.entries(patientChart.medications).length} Prescription${
          patientChart.length === 1 ? "" : "s"
        }`,
      );
    } else setTitleText("");
  }, [patientChart]);

  const theme = useTheme();

  return (
    <Widget>
      <WidgetTitle title={"Medications"} text={titleText} loading={patientChart == null}>
        <OmniResponsiveButton
          tooltipTitle="Add New Medication"
          buttonText="Medication"
          loading={patientChart == null || patientChart.conditions == null}
          onClick={() => {
            setSelectedMedication("");
          }}
        />
      </WidgetTitle>

      {patientChart == null && <WidgetSkeleton />}
      {patientChart != null && (
        <WidgetList>
          {Object.entries(patientChart.medications).map((medication, index) => {
            return (
              <ListItem
                key={`medication${index}`}
                disableGutters={true}
                sx={{
                  padding: theme.spacing(1),
                  paddingTop: 0,
                  paddingBottom: 0,
                  marginBottom: theme.spacing(1),
                  borderRadius: theme.spacing(1),
                  backgroundColor: alpha(theme.palette.info.main, 0.75),
                  color: theme.palette.info.contrastText,
                  boxShadow: theme.shadows[3],
                  userSelect: "none",
                  webkitUserSelect: "none",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: emphasize(theme.palette.info.main, 0.5),
                  },
                }}
                onClick={() => {
                  setSelectedMedication(medication[0]);
                }}
              >
                <Tooltip
                  disableInteractive
                  enterDelay={500}
                  enterNextDelay={500}
                  title={medication[1].note.length > 0 ? medication[1].note : "No Notes"}
                  placement="right"
                  TransitionComponent={Zoom}
                  TransitionProps={{ timeout: 200, easing: "ease-in-out" }}
                >
                  <OmniAvatar
                    sx={{
                      width: 36,
                      height: 36,
                      fontSize: "1.8rem",
                      border: `2px solid ${theme.palette.info.contrastText}`,
                    }}
                  >
                    <SvgIcon
                      sx={{
                        height: 36,
                        width: 36,
                        color: theme.palette.info.main,
                        backgroundColor: theme.palette.info.contrastText,
                      }}
                    >
                      {getMedicationFormPath(
                        medication[1].note.includes("tablet") ? "tablet" : medication[1].form,
                      )}
                    </SvgIcon>
                  </OmniAvatar>
                </Tooltip>
                <ListItemText
                  primary={
                    <Typography
                      variant="h6"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        marginLeft: theme.spacing(1),
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: theme.palette.info.contrastText,
                        marginTop: 0,
                      }}
                    >{`${medication[0]} ${
                      medication[1].dosage != null ? medication[1].dosage : ""
                    }`}</Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      sx={{
                        marginLeft: theme.spacing(1),
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        marginTop: "-6px",
                        color: theme.palette.info.contrastText,
                        minimumHeight: 20,
                      }}
                    >
                      {"Take " + medication[1].note.split(/,(.+)/)[1]}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </WidgetList>
      )}
      {selectedMedication != null && (
        <PatientMedicationDialog
          open={selectedMedication != null}
          dialogTitle={`${selectedMedication === "" ? "Add" : "Edit"} Medication`}
          medication={selectedMedication}
          allMedications={deepCopyObject(patientChart.medications)}
          rowCount={3}
          onAccept={medication => {
            //the returned medication is a deepCopiedObject, this is rad,
            //it should be the standard contract for dialog returns.
            props.updatePatientMedicationsRequest(medication);
            setSelectedMedication(null);
          }}
          onClose={() => {
            setSelectedMedication(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  patientChart: state.patientSession.patientChart,
});

const mapDispatchToProps = {
  updatePatientMedicationsRequest: patientSessionActions.updatePatientMedicationsRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(PatientMedications);
